import { IConfigurableReport } from "../Contracts/IConfigurableReport";
import { AnalysisSource } from "../Enums/AnalysisSource";
import { AnalysisStatus } from "../Enums/AnalysisStatus";
import { ReportType } from "../Models/IReportType";
import {
    IAnalysisItem,
    IBatch,
    IGCPBigQueryJob,
    ISegment,
} from "../Models/Models";

export const segmentFormatter = (
    name: string,
    segments: ISegment[]
): string => {
    const foundSegment: ISegment = segments.find((seg: ISegment) => {
        return seg.segmentKey === name;
    });
    return foundSegment
        ? `${foundSegment.segmentKey}: ${foundSegment.name}`
        : /** this shouldn't be hit, but covering it just in case */
        "n/a";
};

export function mapBatchesToAnalysisItems(batches: IBatch[]): IAnalysisItem[] {
    return batches.map((report: IBatch) => {
        return {
            id: report.id,
            createdByUserId: report.createdByUserID,
            cid: report.cid,
            startDateTime: report.startDateTime,
            endDateTime: report.finishedDateTime,
            createdDateTime: report.createdDateTime,
            source: AnalysisSource.Nexus,
            studyAreaCount: report.jobCount,
            status: report.status,
            name: report.name,
            type: report.overallJobType,
        } as IAnalysisItem;
    });
}

export function mapGcpJobsToAnalysisItems(
    jobs: IGCPBigQueryJob[]
): IAnalysisItem[] {
    return jobs.map((job: IGCPBigQueryJob) => {
        return {
            id: job.id,
            jobId: job.jobId,
            createdByUserId: job.uid,
            cid: job.cid,
            startDateTime: job.queryFromDateTime,
            endDateTime: job.queryToDateTime,
            createdDateTime: new Date(job.createdDateTime.toString()),
            source: mapGcpJobType(job.jobType),
            studyAreaCount: 1,
            status: mapGcpJobStatusToAnalysisStatus(job),
            name: job.title,
            type: new ReportType(getGcpFriendlyReportType(job.jobType)),
        } as IAnalysisItem;
    });
}

export function mapConfigurableReportsToAnalysisItems(
    reports: IConfigurableReport[]
): IAnalysisItem[] {
    return reports.map((report: IConfigurableReport) => {
        return {
            id: report.id,
            jobId: report.jobId,
            createdByUserId: report.createdByUserId,
            cid: report.cid,
            startDateTime: new Date(report.startDateTime),
            endDateTime: new Date(report.startDateTime),
            createdDateTime: new Date(report.startDateTime),
            source: report.source,
            studyAreaCount: report.studyAreaCount,
            status: report.status,
            name: report.name,
            type: {
                typeID: report.reportType.typeId,
                name: `${report.reportType.name}${report.studyAreaCount > 1 ? " (Comparison)" : ""}`,
                featureID: null,
                description: "Configurable Report Type",
                modifiedDateTime: null,
                iconName: "",
            },
        } as IAnalysisItem;
    });
}

export function mapGcpJobType(jobType: string): AnalysisSource {
    let type: AnalysisSource = AnalysisSource.Insight;
    switch (jobType.toLowerCase()) {
        case "retail_match": {
            type = AnalysisSource.RetailMatch;
            break;
        }
    }
    return type;
}

export function mapGcpJobStatusToAnalysisStatus(
    job: IGCPBigQueryJob
): AnalysisStatus {
    let status: AnalysisStatus;

    switch (job.status) {
        case "RUNNING": {
            status = AnalysisStatus.Running;
            break;
        }
        case "CACHING": {
            status = AnalysisStatus.Caching;
            break;
        }
        case "DONE": {
            status = AnalysisStatus.Complete;
            break;
        }
        case "TOO_FEW_RECORDS": {
            status = AnalysisStatus.TooFewRecords;
            break;
        }
        case "ERROR": {
            status = AnalysisStatus.CompleteWithErrors;
            break;
        }
        default:
            status = AnalysisStatus.Pending;
            break;
    }
    return status;
}
export function getGcpFriendlyReportType(jobType: string): string {
    let friendly = "n/a";
    switch (jobType.toLowerCase()) {
        case "retail_match": {
            friendly = "Match Analysis";
            break;
        }
        case "mobilytics": {
            friendly = "Mobilytics";
            break;
        }
    }
    return friendly;
}
